import { useState, useEffect, useCallback } from "react";
import { useUser } from "@/context/UserContext";
import { supabase } from "@/lib/supabase";
import type { Employee } from "@/types/employee";
import type { SalaryApplication } from "@/types/salary";

interface ToastState {
  message: string;
  type: "success" | "error";
}

const statusLabelMap: Record<string, string> = {
  pending: "審核中",
  approved: "已核准",
  rejected: "已駁回",
};

const statusColorMap: Record<string, string> = {
  pending: "bg-secondary-100 text-secondary-800",
  approved: "bg-primary-100 text-primary-700",
  rejected: "bg-accent-100 text-accent-700",
};

export default function MyApplicationsPage() {
  const { employeeId, userName, userEmail, userRole } = useUser();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [applications, setApplications] = useState<SalaryApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  const [form, setForm] = useState({
    work_hours: "",
    amount: "",
    notes: "",
  });

  const fetchData = useCallback(async () => {
    if (!employeeId) {
      setLoading(false);
      return;
    }
    try {
      const [empRes, appsRes] = await Promise.all([
        supabase.from("employees").select("*").eq("id", employeeId).maybeSingle(),
        supabase
          .from("salary_applications")
          .select("*")
          .eq("employee_id", employeeId)
          .order("created_at", { ascending: false }),
      ]);

      if (empRes.data) setEmployee(empRes.data as Employee);
      if (appsRes.data) setApplications(appsRes.data as SalaryApplication[]);
    } catch {
      // ignore
    }
    setLoading(false);
  }, [employeeId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSubmit = async () => {
    if (!employeeId || !employee) {
      showToast("無法送出：未找到員工資料", "error");
      return;
    }
    const workHours = Number(form.work_hours);
    const amount = Number(form.amount);
    if (!workHours || workHours <= 0) {
      showToast("請輸入有效的工作時數", "error");
      return;
    }
    if (!amount || amount <= 0) {
      showToast("請輸入有效的提領金額", "error");
      return;
    }

    setSubmitting(true);

    try {
      const { error: insertErr } = await supabase
        .from("salary_applications")
        .insert({
          employee_id: employeeId,
          work_hours: workHours,
          amount,
          currency: "NT$",
          bank_name: employee.bank_name || null,
          bank_branch: employee.bank_branch || null,
          bank_code: employee.bank_code || null,
          bank_account_name: employee.bank_account_name || null,
          bank_account_number: employee.bank_account_number || null,
          notes: form.notes || null,
          status: "pending",
        });

      if (insertErr) {
        showToast("送出失敗：" + insertErr.message, "error");
      } else {
        showToast("申請已送出，請等候財務部審核", "success");
        setForm({ work_hours: "", amount: "", notes: "" });
        fetchData();
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "送出失敗";
      showToast("送出失敗：" + msg, "error");
    }

    setSubmitting(false);
  };

  const submittedCount = applications.length;
  const pendingAmount = applications
    .filter((a) => a.status === "pending")
    .reduce((sum, a) => sum + (a.amount || 0), 0);
  const approvedAmount = applications
    .filter((a) => a.status === "approved")
    .reduce((sum, a) => sum + (a.amount || 0), 0);

  const formatMoney = (n: number) =>
    "NT$ " + n.toLocaleString("zh-TW");

  const fieldClass =
    "w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 bg-background-50";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-primary-500 text-3xl" />
          <p className="text-sm text-foreground-500">載入中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-[1440px] mx-auto">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-2 ${
            toast.type === "error"
              ? "bg-accent-500 text-background-50"
              : "bg-foreground-800 text-background-50"
          }`}
        >
          <i
            className={`${toast.type === "error" ? "ri-error-warning-line" : "ri-check-line"} text-primary-400`}
          />
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div>
        <h1
          className="text-xl font-bold text-foreground-900"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          我的申請
        </h1>
        <p className="text-sm text-foreground-500 mt-1">
          員工薪資提領申請，由財務部審核
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-background-50 border border-background-200 rounded-xl p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
            <i className="ri-file-list-3-line text-primary-500 text-xl" />
          </div>
          <div>
            <p className="text-xs text-foreground-500">已送出申請</p>
            <p className="text-xl font-bold text-foreground-900 mt-0.5">
              {submittedCount} 筆
            </p>
          </div>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-secondary-50 flex items-center justify-center shrink-0">
            <i className="ri-time-line text-secondary-500 text-xl" />
          </div>
          <div>
            <p className="text-xs text-foreground-500">審核中金額</p>
            <p className="text-xl font-bold text-foreground-900 mt-0.5">
              {formatMoney(pendingAmount)}
            </p>
          </div>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
            <i className="ri-check-double-line text-primary-500 text-xl" />
          </div>
          <div>
            <p className="text-xs text-foreground-500">已核准累計</p>
            <p className="text-xl font-bold text-foreground-900 mt-0.5">
              {formatMoney(approvedAmount)}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Left: Application Form */}
        <div className="lg:col-span-2">
          <div className="bg-background-50 border border-background-200 rounded-xl p-5 md:p-6">
            <h2 className="text-base font-bold text-foreground-900 mb-1 flex items-center gap-2">
              <i className="ri-add-circle-line text-primary-500" />
              新增提領申請
            </h2>
            <p className="text-xs text-foreground-500 mb-5">
              系統將自動帶出您登記的銀行帳戶
            </p>

            {/* Applicant Info */}
            <div className="mb-4">
              <label className="block text-xs text-foreground-600 mb-1.5">
                申請人
              </label>
              <div className="bg-background-100 rounded-lg px-3 py-2.5 text-sm text-foreground-700">
                <p className="font-medium">{employee?.name || userName}</p>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {employee?.employee_code || "—"} ·{" "}
                  {employee?.department || "—"} ·{" "}
                  {userRole === "admin"
                    ? "超級管理員"
                    : userRole === "manager"
                      ? "主管"
                      : userRole === "trial"
                        ? "試用期業務"
                        : "員工"}
                </p>
              </div>
            </div>

            {/* Bank Account */}
            <div className="mb-4">
              <label className="block text-xs text-foreground-600 mb-1.5">
                <i className="ri-bank-line mr-1" />
                匯款銀行帳戶（自動帶出）
              </label>
              <div className="bg-background-100 rounded-lg px-3 py-2.5 text-sm text-foreground-700 min-h-[40px]">
                {employee?.bank_name && employee?.bank_account_number ? (
                  <p>
                    {employee.bank_name} · {employee.bank_account_number}
                  </p>
                ) : (
                  <p className="text-foreground-400">— —</p>
                )}
              </div>
              {!employee?.bank_name && (
                <p className="text-xs text-accent-500 mt-1.5">
                  <i className="ri-error-warning-line mr-1" />
                  尚未設定薪資帳戶，請先至「個人中心」填寫銀行資料
                </p>
              )}
            </div>

            {/* Work Hours & Amount */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs text-foreground-600 mb-1.5">
                  工作時數
                </label>
                <input
                  type="number"
                  value={form.work_hours}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, work_hours: e.target.value }))
                  }
                  placeholder="例：160"
                  className={fieldClass}
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-600 mb-1.5">
                  提領金額（NT$）
                </label>
                <input
                  type="number"
                  value={form.amount}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, amount: e.target.value }))
                  }
                  placeholder="例：30000"
                  className={fieldClass}
                />
              </div>
            </div>

            {/* Notes */}
            <div className="mb-5">
              <label className="block text-xs text-foreground-600 mb-1.5">
                備註說明
              </label>
              <textarea
                value={form.notes}
                onChange={(e) =>
                  setForm((p) => ({ ...p, notes: e.target.value }))
                }
                placeholder="例：當月薪資、業績獎金、加班費…"
                rows={3}
                className={`${fieldClass} resize-none`}
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting || !employee?.bank_name}
              className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-send-plane-line" />
              {submitting ? "送出中..." : "送出申請"}
            </button>
          </div>
        </div>

        {/* Right: History */}
        <div className="lg:col-span-3">
          <div className="bg-background-50 border border-background-200 rounded-xl p-5 md:p-6">
            <h2 className="text-base font-bold text-foreground-900 mb-1">
              我的歷史申請紀錄
            </h2>
            <p className="text-xs text-foreground-500 mb-4">
              共 {applications.length} 筆
            </p>

            {applications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <i className="ri-file-list-3-line text-foreground-300 text-4xl mb-3" />
                <p className="text-sm text-foreground-400">
                  尚無申請紀錄，請於左側送出第一筆申請。
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {applications.map((app) => (
                  <div
                    key={app.id}
                    className="border border-background-200 rounded-lg p-4 hover:border-background-300 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-mono text-foreground-400">
                            {app.id.slice(0, 8)}
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColorMap[app.status]}`}
                          >
                            {statusLabelMap[app.status]}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-2 gap-x-4 text-sm">
                          <div>
                            <span className="text-foreground-500 text-xs block">
                              工作時數
                            </span>
                            <span className="text-foreground-900 font-medium">
                              {app.work_hours || "—"} 小時
                            </span>
                          </div>
                          <div>
                            <span className="text-foreground-500 text-xs block">
                              提領金額
                            </span>
                            <span className="text-foreground-900 font-medium">
                              {formatMoney(app.amount)}
                            </span>
                          </div>
                          <div>
                            <span className="text-foreground-500 text-xs block">
                              銀行帳戶
                            </span>
                            <span className="text-foreground-900">
                              {app.bank_name || "—"}
                            </span>
                          </div>
                          <div>
                            <span className="text-foreground-500 text-xs block">
                              申請時間
                            </span>
                            <span className="text-foreground-900">
                              {new Date(app.created_at).toLocaleDateString(
                                "zh-TW"
                              )}
                            </span>
                          </div>
                        </div>
                        {app.notes && (
                          <p className="text-xs text-foreground-500 mt-2 bg-background-100 rounded-md px-2.5 py-1.5 inline-block">
                            {app.notes}
                          </p>
                        )}
                        {app.status === "rejected" && app.reject_reason && (
                          <p className="text-xs text-accent-600 mt-2 bg-accent-50 rounded-md px-2.5 py-1.5 inline-block">
                            <i className="ri-error-warning-line mr-1" />
                            駁回原因：{app.reject_reason}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}